import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ClipboardText, Copy, PencilSimple, Plus, Prohibit } from '@phosphor-icons/react'
import { Badge, Banner, Button, useToast } from '@/ui'
import { errorMessage } from '../api/core'
import {
  DECLARATION_STATE_LABEL,
  LEVEL_LABEL,
  LOCATION_KIND_LABEL,
  LOCATION_ROLE_LABEL,
  useDeactivateLocation,
  useLocationContents,
  useRequestCount,
  type LocationRow,
} from '../api/store'
import { bayNeedsDeclaration, bayState, childLevels, type TreeNode } from '../model/locationTree'
import { humanize } from '../labels'
import { ActionButton } from './Can'
import { ConfirmDialog } from './ConfirmDialog'
import { DetailDrawer } from './DetailDrawer'
import { QueryState } from './Layout'
import { PhotoViewer } from './PhotoViewer'
import { BayStateBadge, RoleBadge } from './LocationTree'
import { Code, Facts, RefLink, Section, Status, Stream, UserName, When } from './bits'

/** One place: details, its label, its bay declaration, what is in it, and what can be done. */
export function LocationDrawer({
  node,
  bay,
  expiring,
  onClose,
  onAddChild,
  onEdit,
}: {
  node: TreeNode
  /** The bay this place is in (itself for a bay). */
  bay: LocationRow | null
  expiring: boolean
  onClose: () => void
  onAddChild: (parent: LocationRow) => void
  onEdit: (row: LocationRow) => void
}) {
  const r = node.row
  const [confirm, setConfirm] = useState<'deactivate' | 'count' | null>(null)
  const [deactivateError, setDeactivateError] = useState<string | null>(null)
  const deactivate = useDeactivateLocation((e) => {
    setDeactivateError(errorMessage(e))
    return true
  })
  const count = useRequestCount()
  const canHaveChildren = childLevels(r.level).length > 0

  return (
    <DetailDrawer
      open
      onClose={onClose}
      eyebrow={LEVEL_LABEL[r.level] ?? r.level}
      title={r.name}
      meta={
        <>
          <Code>{r.code}</Code>
          <RoleBadge role={r.role} />
          <BayStateBadge row={r} expiring={expiring} />
          {!r.active && <Badge tone="neutral">Not in use</Badge>}
        </>
      }
      actions={
        <>
          <ActionButton perm="stock:request-count" hide size="sm" variant="ghost" leadingIcon={<ClipboardText />} onClick={() => setConfirm('count')} disabled={!r.active}>
            Request a count
          </ActionButton>
          {r.active && (
            <ActionButton
              perm="locations:delete"
              hide
              size="sm"
              variant="ghost"
              leadingIcon={<Prohibit />}
              onClick={() => {
                setDeactivateError(null)
                setConfirm('deactivate')
              }}
            >
              Take out of use
            </ActionButton>
          )}
          <ActionButton perm="locations:update" hide size="sm" variant="secondary" leadingIcon={<PencilSimple />} onClick={() => onEdit(r)}>
            Edit
          </ActionButton>
          {canHaveChildren && (
            <ActionButton
              perm="locations:create"
              hide
              size="sm"
              leadingIcon={<Plus />}
              onClick={() => onAddChild(r)}
              blockedReason={r.active ? null : 'This place is not in use'}
            >
              Add a place
            </ActionButton>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <p className="text-[14px] text-muted">{node.path}</p>
        <Facts
          columns={3}
          items={[
            { label: 'Kind', value: r.kind ? (LOCATION_KIND_LABEL[r.kind] ?? humanize(r.kind)) : '—' },
            { label: 'Used for', value: LOCATION_ROLE_LABEL[r.role ?? 'STORAGE']?.en },
            { label: 'Capacity', value: r.capacityKg !== null ? `${r.capacityKg.toLocaleString('en-IN')} kg` : '—', mono: true },
            { label: 'Items here', value: node.itemsBelow, mono: true },
            { label: 'Places below', value: node.childCount, mono: true },
            { label: 'Status', value: r.active ? 'In use' : 'Not in use' },
          ]}
        />
        <LabelBox payload={r.labelPayload} code={r.code} />
        {bay && <BayDeclaration bay={bay} expiring={expiring} isBay={bay.id === r.id} />}
        <Contents id={r.id} />
      </div>

      <ConfirmDialog
        open={confirm === 'deactivate'}
        onClose={() => setConfirm(null)}
        title={`Take ${r.name} out of use?`}
        description={
          node.childCount > 0
            ? 'It and every place below it stop being offered on the phones. Codes are kept, never reused.'
            : 'It stops being offered on the phones. The code is kept, never reused.'
        }
        confirmLabel="Take out of use"
        tone="danger"
        loading={deactivate.isPending}
        onConfirm={async () => {
          await deactivate.mutateAsync(r.id)
          setConfirm(null)
        }}
      >
        {deactivateError && <Banner tone="error" title="Not taken out of use">{deactivateError}</Banner>}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm === 'count'}
        onClose={() => setConfirm(null)}
        title={`Ask for a count of ${r.name}`}
        description="The keeper sees the request on the store phone until the place is counted."
        confirmLabel="Request count"
        loading={count.isPending}
        reason={{
          label: 'Note for the keeper',
          required: false,
          placeholder: 'e.g. Monthly count',
          pattern: /^[\s\S]{0,200}$/,
          patternMessage: 'At most 200 characters',
        }}
        onConfirm={async (note) => {
          await count.mutateAsync({ id: r.id, note })
          setConfirm(null)
        }}
      />
    </DetailDrawer>
  )
}

function LabelBox({ payload, code }: { payload: string | null; code: string }) {
  const toast = useToast()
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(payload ?? '')
      toast.success('Label text copied')
    } catch {
      toast.error('Could not copy', 'Select the text and copy it by hand.')
    }
  }
  return (
    <Section title="Label" eyebrow="QR text to print" bodyClassName="flex flex-wrap items-center justify-between gap-3">
      {payload ? (
        <>
          <div className="min-w-0">
            <div className="select-all break-all font-mono text-[17px] text-ink">{payload}</div>
            <div className="mt-0.5 text-[12.5px] text-muted">
              Print with <span className="font-mono">{code}</span> under the code.
            </div>
          </div>
          <Button size="sm" variant="secondary" leadingIcon={<Copy />} onClick={() => void copy()}>
            Copy
          </Button>
        </>
      ) : (
        <p className="text-[14px] text-muted">No label is bound to this place.</p>
      )}
    </Section>
  )
}

function BayDeclaration({ bay, expiring, isBay }: { bay: LocationRow; expiring: boolean; isBay: boolean }) {
  const d = bay.declaration
  const state = bayState(d, expiring)
  return (
    <Section
      title="No-fumigant declaration"
      eyebrow={isBay ? 'This bay' : `Bay · ${bay.name}`}
      actions={
        <Link to={`/c/declarations?tab=all&bay=${bay.id}`} className="text-[13px] text-ink underline decoration-rule underline-offset-2 hover:decoration-amber">
          History
        </Link>
      }
    >
      {!d ? (
        <p className="text-[14px] text-muted">
          {bay.role === 'CONVENTIONAL'
            ? 'Not needed. Ordinary (red tie) produce is kept here.'
            : !bayNeedsDeclaration(bay.role)
              ? `Not needed. A ${(LOCATION_ROLE_LABEL[bay.role ?? '']?.en ?? 'place').toLowerCase()} never holds brand bags.`
              : 'Never declared. Brand produce put here goes on hold until the keeper signs a declaration on the phone.'}
        </p>
      ) : (
        <div className="flex flex-wrap gap-4">
          <div className="min-w-0 flex-1">
            <Facts
              columns={2}
              items={[
                { label: 'State', value: <Status status={state === 'OK' ? 'OK' : state === 'EXPIRING' ? 'HOLD' : 'FAILED'} label={DECLARATION_STATE_LABEL[state]} /> },
                { label: 'Valid to', value: <When at={d.validTo} dateOnly /> },
                { label: 'Signed by', value: <UserName id={d.signedById} /> },
                { label: 'Signed', value: <When at={d.signedAt} /> },
              ]}
            />
          </div>
          <PhotoViewer photo={d.photo} alt={`Declaration photo · ${bay.name}`} size="md" />
        </div>
      )}
    </Section>
  )
}

function Contents({ id }: { id: string }) {
  const q = useLocationContents(id)
  const lots = q.data?.lots ?? []
  const samples = q.data?.samples ?? []
  return (
    <Section title="What is here" eyebrow="Here and below">
      <QueryState query={q} label="the contents">
        {lots.length === 0 && samples.length === 0 ? (
          <p className="text-[14px] text-muted">Empty.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {lots.length > 0 && (
              <table className="w-full text-left text-[14px]">
                <caption className="sr-only">Lots</caption>
                <thead>
                  <tr className="font-mono text-[10.5px] uppercase tracking-label text-muted">
                    <th className="py-1 font-bold">Lot</th>
                    <th className="py-1 font-bold">Stream</th>
                    <th className="py-1 font-bold">Hold</th>
                    <th className="py-1 text-right font-bold">Bags here</th>
                  </tr>
                </thead>
                <tbody>
                  {lots.map((l) => (
                    <tr key={l.unitId} className="border-t border-rule/60">
                      <td className="py-1.5">
                        <RefLink to={`/c/lots/${l.unitId}`}>{l.unitCode}</RefLink>
                      </td>
                      <td className="py-1.5">
                        <Stream stream={l.stream} />
                      </td>
                      <td className="py-1.5">
                        {l.hold === 'NONE' ? <span className="text-muted">—</span> : <Status status={l.hold} label={l.holdReasons.length ? `On hold · ${l.holdReasons.map(humanize).join(', ')}` : undefined} />}
                      </td>
                      <td className="py-1.5 text-right font-mono tabular">
                        {l.bagsHere}
                        <span className="text-muted"> / {l.bagsTotal}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {samples.length > 0 && (
              <div>
                <div className="mb-1 font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">Samples</div>
                <ul className="divide-y divide-rule/60">
                  {samples.map((s) => (
                    <li key={s.sampleId} className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-[14px]">
                      <span>
                        {humanize(s.kind)} <Code className="ml-1 text-muted">{s.sealPayload ?? ''}</Code>
                      </span>
                      <Status status={s.status} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </QueryState>
    </Section>
  )
}
